
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";

// Password schema
export const passwordSchema = z
  .object({
    email: z.string().email({ message: "Please enter a valid email address" }),
    password: z
      .string()
      .min(8, { message: "Password must be at least 8 characters" }),
    confirmPassword: z.string(),
    firstName: z.string().min(1, { message: "First name is required" }),
    lastName: z.string().min(1, { message: "Last name is required" }),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

// Form data type
export type PasswordFormData = z.infer<typeof passwordSchema>;

// Loading state
export const LoadingState: React.FC = () => (
  <Card className="w-full max-w-md mx-auto">
    <CardHeader>
      <h2 className="text-xl font-semibold text-center">Loading...</h2>
    </CardHeader>
    <CardContent className="flex justify-center py-12">
      <Spinner size="lg" />
    </CardContent>
  </Card>
);

// Invalid token state
export const InvalidTokenState: React.FC = () => (
  <Card className="w-full max-w-md mx-auto">
    <CardHeader>
      <h2 className="text-xl font-semibold text-center">Invalid invitation</h2>
    </CardHeader>
    <CardContent>
      <Alert>
        <AlertDescription>
          Your invitation link does not match our records. 
          <br/><br/>Please check that you copied the entire link correctly, or contact 
          the person who sent you the invitation.
        </AlertDescription>
      </Alert>
    </CardContent>
  </Card>
);

// Expired token state
export const ExpiredTokenState: React.FC = () => (
  <Card className="w-full max-w-md mx-auto">
    <CardHeader>
      <h2 className="text-xl font-semibold text-center">Invitation expired</h2>
    </CardHeader>
    <CardContent>
      <Alert>
        <AlertDescription>
          This invitation has expired. <br/><br/>
          Please contact the team member who sent you the invitation 
          to request a new one.
        </AlertDescription>
      </Alert>
    </CardContent>
  </Card>
);

// Already accepted state
export const AlreadyAcceptedState: React.FC = () => (
  <Card className="w-full max-w-md mx-auto">
    <CardHeader>
      <h2 className="text-xl font-semibold text-center">Invitation already used</h2>
    </CardHeader>
    <CardContent>
      <Alert>
        <AlertDescription>
          This invitation has already been accepted. 
          <br/><br/>
          If you need to access your account, please go to the login page.
        </AlertDescription>
      </Alert>
    </CardContent>
    <CardFooter className="justify-center">
      <Button onClick={() => window.location.href = "/login"}>
        Go to login
      </Button>
    </CardFooter>
  </Card>
);

// Valid token state with account creation form
export const ValidTokenState: React.FC<{
  form: UseFormReturn<PasswordFormData>;
  handleCreateAccount: (data: PasswordFormData) => void;
  isSubmitting: boolean;
}> = ({ form, handleCreateAccount, isSubmitting }) => (
  <Card className="w-full max-w-md mx-auto">
    <CardHeader>
      <h2 className="text-xl font-semibold text-center">Create your account</h2>
      <p className="text-center text-muted-foreground">
        Fill in the details below to complete your registration
      </p>
    </CardHeader>
    <CardContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleCreateAccount)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input placeholder="John" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="your.email@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="********" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="********" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
            Create account
          </Button>
        </form>
      </Form>
    </CardContent>
  </Card>
);

// Creating account state
export const CreatingAccountState: React.FC = () => (
  <Card className="w-full max-w-md mx-auto">
    <CardHeader>
      <h2 className="text-xl font-semibold text-center">Creating your account...</h2>
    </CardHeader>
    <CardContent className="flex justify-center py-12">
      <Spinner size="lg" />
    </CardContent>
  </Card>
);

// Account created state
export const AccountCreatedState: React.FC = () => (
  <Card className="w-full max-w-md mx-auto">
    <CardHeader>
      <h2 className="text-xl font-semibold text-center">Account created!</h2>
    </CardHeader>
    <CardContent>
      <Alert>
        <AlertDescription>
          <div>Your account has been created successfully.</div>
          <div>A verification email has been sent to your email address. Please verify your email.</div>
        </AlertDescription>
      </Alert>
    </CardContent>
    <CardFooter className="justify-center">
      <Button onClick={() => window.location.href = "/login"}>
        Go to login
      </Button>
    </CardFooter>
  </Card>
);

// Note: LocationEditingState is imported directly from WelcomeLocationEditingState.tsx
// and no longer needs to be defined here
